// The world view: streamed chunks (ground tilemaps + depth-sorted objects), people, input.
// Art comes from /art/* (tools/build-art.py); anything missing falls back to flat shapes.
import Phaser from 'phaser';
import { CHUNK, CHUNKS_X, G, GROUNDS, NET_OBJS, OBJ, POWER_OBJS, OBJECTS, SWING_TIME, TILE, VIEW_CHUNKS, harvestable } from '../../shared/content.ts';
import { BUFFS, VARIETY_HP, type BuffId } from '../../shared/farming.ts';
const OBJ_COOKING_FIRE = OBJ.COOKING_FIRE;
import { ITEMS, type ItemId } from '../../shared/items.ts';
import { PackUi, setEssence, setHp } from './packUi.ts';
import { Sfx } from './sfx.ts';
import { Settings } from './settings.ts';
import { Lighting, type Light } from './lighting.ts';
import { dayNumber, daylight, timeOfDay } from '../../shared/time.ts';
import { DUNGEONS, dungeonAt } from '../../shared/dungeons.ts';
import { HEROES } from '../../shared/heroes.ts';
import type { MobSnap, PlayerSnap, ServerMsg } from '../../shared/protocol.ts';
import type { Net } from './net.ts';
import { GroundEdges, PAD, extrudedGround } from './groundEdges.ts';
import { Minimap } from './minimap.ts';
import { SpellBar } from './spellBar.ts';

const FEET = 115 / 128;
const COLS = 25; // frames per heading row in a character sheet
// Neighbours that make a stone wall run north-south (ruin walls count, so repairs line up).
const WALL_OBJS = new Set<number>([OBJ.STONE_WALL, OBJ.RUIN_WALL]);

type Chunk = {
  cx: number;
  cy: number;
  ground: Uint8Array;
  obj: Uint8Array;
  map: Phaser.Tilemaps.Tilemap;
  objs: Map<number, Phaser.GameObjects.Image>;
  /** Tilled-soil patches drawn under crops. */
  soils: Map<number, Phaser.GameObjects.Image>;
  /** Soft transitions where grounds meet (client/src/groundEdges.ts). */
  edges: Phaser.GameObjects.Blitter | null;
};

type Actor = {
  id: number;
  hero: string;
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  goalX: number;
  goalY: number;
  face: number;
  anim: PlayerSnap['anim'];
  seen: number;
  held: ItemId | null;
  tool: Phaser.GameObjects.Image | null;
};

type MobActor = {
  id: number;
  slug: string;
  name: string;
  sprite: Phaser.GameObjects.Sprite;
  bar: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  goalX: number;
  goalY: number;
  face: number;
  anim: MobSnap['anim'] | '';
  hp: number;
  max: number;
  elite: boolean;
  scale: number;
  seen: number;
  /** Dungeon bosses: side-view art from art/boss-<sheet>.png, flipped to face left. */
  boss?: string;
  title?: string;
  phase?: number;
};

/** Which projectile art each kind of bolt flies as (looks from build-art.py SHOT_NAMES). */
const SHOT_LOOK: Record<string, string> = { bolt: 't-red', lance: 'b-red', arcane: 'c-ice', wand: 'b-violet', skull: 'c-acid', fireball: 'c-fire', siphon: 'b-green', dragonfire: 'c-ember' };
const SHOT_SCALE: Record<string, number> = { bolt: 2.2, lance: 3, arcane: 2.2, wand: 1.8, skull: 3, fireball: 3.6, siphon: 2.4, dragonfire: 3.2 };
/** Ritual stones: label and glow per ritual (shared/blood.ts RITUALS). */
const RITUAL_LOOK: Record<string, { name: string; color: string; tint: number }> = {
  grove: { name: 'Green Grove', color: '#9ae07a', tint: 0xffffff },
  ward: { name: 'Warding Veil', color: '#9ac8ff', tint: 0x9ac8ff },
  well: { name: 'Well of Suffering', color: '#ff7a8a', tint: 0xff5060 },
};
const BOOM_TINT: Record<string, number> = { arcane: 0x4a8aff, wand: 0xa060ff, fire: 0xff8030, meteor: 0xff9040, big: 0xffaa46, small: 0xff8c3c };
/** Projectiles (art/shots.png, 16px frames, pointing right): (ax, ay) is the bright head. */
type ShotMeta = { cols: number; looks: Record<string, { row: number; n: number; fps: number; ax: number; ay: number; rgb: [number, number, number] }> };
/** Blasts (art/boom-<look>.png): r is the biggest content radius in art px, for scaling to a real radius. */
type BoomMeta = Record<string, { w: number; h: number; n: number; fps: number; r: number }>;
type BossMeta = Record<string, { frame: [number, number]; pivot: [number, number]; anims: Record<string, [number, number, number, boolean, number]> }>;

/** Unit vector per facing (0 = east, clockwise). */
const FACE_DIR = Array.from({ length: 8 }, (_, f) => ({ x: Math.cos((f * Math.PI) / 4), y: Math.sin((f * Math.PI) / 4) }));

const vhash = (x: number, y: number) => {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
};

export class WorldScene extends Phaser.Scene {
  private net!: Net;
  private myId = 0;
  private minimap!: Minimap;
  private groundEdges!: GroundEdges;
  private spellBar!: SpellBar;
  private chunks = new Map<number, Chunk>();
  private actors = new Map<number, Actor>();
  private groundFrames: Record<number, number[]> = {};
  private objFrames: Record<string, string[]> = {};
  private missing = new Set<string>();
  private heroLoading = new Set<string>();
  private hover: Phaser.GameObjects.Image | null = null;
  private keys!: Record<'up' | 'down' | 'left' | 'right' | 'w' | 'a' | 's' | 'd', Phaser.Input.Keyboard.Key>;
  private sentKeys = '';
  private lastHold = 0;
  private holding = false;
  private snapCount = 0;
  sfx!: Sfx;
  packUi!: PackUi;
  private stepT = 0;
  private mobs = new Map<number, MobActor>();
  private hoverMob: MobActor | null = null;
  private wounded = 0;
  private fullHp = 100;
  private lighting!: Lighting;
  /** Light-giving objects in loaded chunks, by tile index. */
  private lamps = new Map<number, { x: number; y: number; r: number; tint?: number }>();
  private fxFrames: Record<string, number> = {};
  private bolts = new Map<number, { sprite: Phaser.GameObjects.Sprite; seen: number; gx: number; gy: number; tint: number }>();
  private shotMeta: ShotMeta = { cols: 1, looks: {} };
  private boomMeta: BoomMeta = {};
  /** Blasts light up the night for a moment: a flash that shrinks over `dur` ms. */
  private flashes: { x: number; y: number; r: number; tint: number; t0: number; dur: number }[] = [];
  private siteLabels = new Map<string, Phaser.GameObjects.Text>();
  private ritualFx = new Map<string, Phaser.GameObjects.Sprite>();
  private worldTime = 0;
  private worldTimeAt = 0;
  private bossMeta: BossMeta = {};
  private drops = new Map<number, { icon: Phaser.GameObjects.Image; glow: Phaser.GameObjects.Ellipse; label: Phaser.GameObjects.Text; name: Phaser.GameObjects.Text; seen: number; x: number; y: number }>();
  private bossLoading = new Set<string>();

  constructor() {
    super('world');
  }

  init(data: { net: Net }): void {
    this.net = data.net;
  }

  preload(): void {
    this.load.on('loaderror', (file: Phaser.Loader.File) => this.missing.add(file.key));
    this.load.image('ground', '/art/ground.png');
    this.load.json('ground-meta', '/art/ground.json');
    this.load.image('objects', '/art/objects.png');
    this.load.json('objects-meta', '/art/objects.json');
    this.load.image('icons', '/art/icons.png');
    this.load.spritesheet('pipe', '/art/pipe.png', { frameWidth: TILE, frameHeight: TILE });
    this.load.spritesheet('cable', '/art/cable.png', { frameWidth: TILE, frameHeight: TILE });
    this.load.json('fx-meta', '/art/fx.json');
    this.load.once('filecomplete-json-fx-meta', (_k: string, _t: string, meta: Record<string, number>) => {
      this.fxFrames = meta;
      for (const name of Object.keys(meta)) this.load.spritesheet(`fx-${name}`, `/art/fx-${name}.png`, { frameWidth: 64, frameHeight: 64 });
    });
    this.load.spritesheet('shots', '/art/shots.png', { frameWidth: 16, frameHeight: 16 });
    this.load.json('shots-meta', '/art/shots.json');
    this.load.json('booms-meta', '/art/booms.json');
    this.load.once('filecomplete-json-booms-meta', (_k: string, _t: string, meta: BoomMeta) => {
      this.boomMeta = meta;
      for (const [look, b] of Object.entries(meta)) this.load.spritesheet(`boom-${look}`, `/art/boom-${look}.png`, { frameWidth: b.w, frameHeight: b.h });
    });
    this.load.json('icons-meta', '/art/icons.json');
    this.load.json('bosses-meta', '/art/bosses.json');
    this.sfx = new Sfx(this);
    this.sfx.preload();
  }

  create(): void {
    this.setupGround();
    extrudedGround(this, 'ground-x', Math.round(this.textures.get('ground').getSourceImage().width / TILE));
    this.groundEdges = new GroundEdges(this, this.groundFrames);
    this.groundEdges.build();
    this.setupObjects();
    this.bossMeta = (this.cache.json.get('bosses-meta') ?? {}) as BossMeta;
    const iconMeta = (this.cache.json.get('icons-meta') ?? {}) as Record<string, [number, number, number, number]>;
    if (this.textures.exists('icons')) for (const [id, [x, y, w, h]] of Object.entries(iconMeta)) this.textures.get('icons').add(id, 0, x, y, w, h);
    this.sfx.load();
    this.sfx.ambience('amb_day');
    this.load.once('complete', () => this.sfx.ambience('amb_day'));
    this.packUi = new PackUi(this.net, this.sfx, iconMeta);
    this.packUi.nearStation = (st) => (st === 'furnace' ? this.nearObj(OBJ.FURNACE, 3) || this.nearObj(OBJ.ALLOY, 3) : this.nearObj(OBJ_COOKING_FIRE, 3));
    this.lighting = new Lighting(this);
    this.minimap = new Minimap();
    this.spellBar = new SpellBar(this.net, () => this.cameras.main.getWorldPoint(this.input.activePointer.x, this.input.activePointer.y));
    for (const [name, n] of Object.entries(this.fxFrames)) {
      if (!this.textures.exists(`fx-${name}`)) continue;
      this.anims.create({ key: `fx-${name}`, frames: this.anims.generateFrameNumbers(`fx-${name}`, { start: 0, end: n - 1 }), frameRate: name === 'bolt' || name === 'arcane' || name === 'wand' ? 18 : 22, repeat: name === 'bolt' || name === 'arcane' || name === 'wand' || name === 'ritual' ? -1 : 0 });
    }
    this.shotMeta = (this.cache.json.get('shots-meta') ?? { cols: 1, looks: {} }) as ShotMeta;
    if (this.textures.exists('shots')) for (const [look, sh] of Object.entries(this.shotMeta.looks)) {
      const start = sh.row * this.shotMeta.cols;
      this.anims.create({ key: `shot-${look}`, frames: this.anims.generateFrameNumbers('shots', { start, end: start + sh.n - 1 }), frameRate: sh.fps, repeat: -1 });
    }
    for (const [look, b] of Object.entries(this.boomMeta)) {
      if (!this.textures.exists(`boom-${look}`)) continue;
      const frames = this.anims.generateFrameNumbers(`boom-${look}`, { start: 0, end: b.n - 1 });
      // The wand's blast is the space swirl played backwards, so it implodes.
      this.anims.create({ key: `boom-${look}`, frames: look === 'wand' ? frames.reverse() : frames, frameRate: b.fps, repeat: 0 });
    }
    // H opens the player's guide (client/public/help.html) in a new tab.
    this.input.keyboard!.on('keydown-H', () => {
      if (document.activeElement?.tagName === 'INPUT') return;
      window.open('/help.html', '_blank', 'noopener');
    });
    this.input.keyboard!.on('keydown-M', () => {
      if (document.activeElement?.tagName === 'INPUT') return;
      toast(this.sfx.toggleMute() ? 'Sound off (M)' : 'Sound on (M)');
    });
    this.cameras.main.setZoom(Phaser.Math.Clamp(Settings.get().zoom, 0.6, 2.5));
    Settings.watch((s) => document.getElementById('help')?.classList.toggle('hidden', !s.showHint));
    document.getElementById('open-settings')?.addEventListener('click', (e) => {
      e.preventDefault();
      Settings.toggle();
    });
    this.cameras.main.setRoundPixels(true);
    (window as unknown as { __reclaim: WorldScene }).__reclaim = this;

    const kb = this.input.keyboard!;
    const K = Phaser.Input.Keyboard.KeyCodes;
    // No key capture: the chat box must still be able to type W, A, S and D.
    this.keys = {
      up: kb.addKey(K.UP, false), down: kb.addKey(K.DOWN, false), left: kb.addKey(K.LEFT, false), right: kb.addKey(K.RIGHT, false),
      w: kb.addKey(K.W, false), a: kb.addKey(K.A, false), s: kb.addKey(K.S, false), d: kb.addKey(K.D, false),
    };

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (p.rightButtonDown()) {
        const { tx, ty } = this.tileAt(p);
        const w = this.cameras.main.getWorldPoint(p.x, p.y);
        this.net.send({ t: 'use', tx, ty, x: Math.round(w.x), y: Math.round(w.y) });
        return;
      }
      if (!p.leftButtonDown()) return;
      (document.activeElement as HTMLElement | null)?.blur();
      const mob = this.mobAt(p);
      if (mob) {
        this.net.send({ t: 'attack', id: mob.id });
        this.holding = false;
        return;
      }
      const { tx, ty } = this.tileAt(p);
      const o = this.objAt(tx, ty);
      if (harvestable(o)) {
        this.net.send({ t: 'act', tx, ty });
        this.holding = false;
      } else {
        this.net.send({ t: 'move', tx, ty });
        this.holding = true;
        this.lastHold = this.time.now;
      }
    });
    this.input.on('pointerup', () => (this.holding = false));
    // Wheel steps through the hotbar (one slot per notch; trackpads fire many small events).
    let wheelAcc = 0;
    this.input.on('wheel', (_p: unknown, _o: unknown, _dx: number, dy: number) => {
      wheelAcc += dy;
      if (Math.abs(wheelAcc) < 40) return;
      this.packUi.step(wheelAcc > 0 ? 1 : -1);
      wheelAcc = 0;
    });
    // + and - zoom (= for + without shift, and the keypad keys).
    document.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      const cam = this.cameras.main;
      if (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd') cam.setZoom(Phaser.Math.Clamp(cam.zoom * 1.12, 0.6, 2.5));
      else if (e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract') cam.setZoom(Phaser.Math.Clamp(cam.zoom / 1.12, 0.6, 2.5));
      else return;
      Settings.set('zoom', cam.zoom);
    });

    setupChat(this.net, kb);
    this.net.onClose = (code, reason) => showStatus(reason || (code === 1006 ? 'Lost the connection. Reload to try again.' : 'Disconnected.'));
    this.net.listen((m) => this.onMsg(m));
  }

  // ---------------------------------------------------------------- art setup

  private setupGround(): void {
    const meta = this.cache.json.get('ground-meta') as Record<string, number[]> | undefined;
    if (meta && this.textures.exists('ground') && !this.missing.has('ground')) {
      for (const [k, v] of Object.entries(meta)) this.groundFrames[Number(k)] = v;
      return;
    }
    // Fallback: one flat square per ground type.
    const c = this.textures.createCanvas('ground-flat', GROUNDS.length * TILE, TILE)!;
    const ctx = c.getContext();
    GROUNDS.forEach((g, i) => {
      ctx.fillStyle = '#' + g.color.toString(16).padStart(6, '0');
      ctx.fillRect(i * TILE, 0, TILE, TILE);
      this.groundFrames[g.id] = [i];
    });
    c.refresh();
    this.textures.renameTexture('ground-flat', 'ground');
  }

  private setupObjects(): void {
    const meta = this.cache.json.get('objects-meta') as Record<string, [number, number, number, number][]> | undefined;
    const tex = this.textures.exists('objects') && !this.missing.has('objects') ? this.textures.get('objects') : null;
    // Pieces that aren't objects themselves (the windmill's sails).
    for (const key of ['windmill_rotor', 'stone_wall_side']) {
      const b = meta?.[key]?.[0];
      if (tex && b) {
        if (!tex.has(key)) tex.add(key, 0, b[0], b[1], b[2], b[3]);
        this.objFrames[key] = [key];
      }
    }
    for (const def of OBJECTS) {
      if (!def?.key) continue; // the table has gaps (crop ids start at 40)
      const boxes = meta?.[def.key];
      if (tex && boxes?.length) {
        this.objFrames[def.key] = boxes.map(([x, y, w, h], i) => {
          const name = `${def.key}:${i}`;
          if (!tex.has(name)) tex.add(name, 0, x, y, w, h);
          return name;
        });
        continue;
      }
      // Fallback: a flat blob, taller for trees.
      const key = `flat-${def.key}`;
      if (!this.textures.exists(key)) {
        const g = this.add.graphics();
        g.fillStyle(def.color, 1);
        if (def.kind === 'tree') {
          g.fillRect(20, 50, 8, 40);
          g.fillCircle(24, 36, 22);
        } else g.fillEllipse(24, 70, def.kind === 'decor' ? 16 : 40, def.kind === 'decor' ? 10 : 30);
        g.generateTexture(key, 48, 90);
        g.destroy();
      }
      this.objFrames[def.key] = [key];
    }
  }

  private ensureHero(slug: string): void {
    const key = `char-${slug}`;
    if (this.textures.exists(key) || this.heroLoading.has(slug)) return;
    this.heroLoading.add(slug);
    this.load.spritesheet(key, `/art/char-${slug}.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.once(`filecomplete-spritesheet-${key}`, () => {
      for (let f = 0; f < 8; f++) {
        const row = f * COLS;
        this.anims.create({ key: `${slug}-walk-${f}`, frames: this.anims.generateFrameNumbers(key, { start: row + 1, end: row + 8 }), frameRate: 13, repeat: -1 });
        this.anims.create({ key: `${slug}-attack-${f}`, frames: this.anims.generateFrameNumbers(key, { start: row + 9, end: row + 16 }), frameRate: 8 / 0.7, repeat: -1 });
        this.anims.create({ key: `${slug}-death-${f}`, frames: this.anims.generateFrameNumbers(key, { start: row + 17, end: row + 24 }), frameRate: 9, repeat: 0 });
      }
      for (const a of this.actors.values()) if (a.hero === slug) {
        a.sprite.setTexture(key, a.face * COLS);
        a.anim = 'idle';
      }
      for (const m of this.mobs.values()) if (m.slug === slug) {
        m.sprite.setTexture(key, m.face * COLS);
        m.anim = '';
      }
    });
    this.load.start();
  }

  // ---------------------------------------------------------------- messages

  private onMsg(m: ServerMsg): void {
    switch (m.t) {
      case 'hello':
        this.myId = m.id;
        setText('hud-name', m.name);
        this.minimap.setSeed(m.seed);
        this.ensureHero(m.hero);
        break;
      case 'chunk':
        this.addChunk(m.cx, m.cy, b64(m.g), b64(m.o));
        break;
      case 'tile':
        this.setTile(m.tx, m.ty, m.o);
        break;
      case 'snap':
        this.onSnap(m.players);
        this.onMobs(m.mobs);
        this.wounded = m.wounded;
        renderBuffs(m.buffs, m.foods);
        this.fullHp = m.fullHp;
        this.worldTime = m.time;
        this.worldTimeAt = this.time.now;
        this.onMagic(m);
        this.onDrops(m.drops);
        setEssence(m.essence, m.essenceCap);
        this.spellBar.update(m.mana, m.manaMax, m.ward, m.cds);
        {
          const me = this.actors.get(this.myId);
          setText('hud-ring', m.ring + (me ? this.claimNameAt(me.goalX, me.goalY) : ''));
        }
        setText('hud-clock', `Day ${dayNumber(m.time)} · ${timeOfDay(m.time)}`);
        break;
      case 'pack':
        this.packUi.update(m.pack);
        break;
      case 'chest':
        this.packUi.showChest(m.slots);
        break;
      case 'machine':
        this.packUi.showMachine(m);
        break;
      case 'term':
        this.packUi.showTerm(m);
        break;
      case 'bench':
        this.packUi.showBench();
        break;
      case 'chestClose':
        this.packUi.hideChest();
        break;
      case 'claims':
        this.drawClaims(m.list);
        this.minimap.setClaims(m.list);
        break;
      case 'spells':
        this.spellBar.setBook(m.known, m.bar);
        break;
      case 'explored':
        this.minimap.setExplored(m.m);
        break;
      case 'hit':
        this.onHit(m.tx, m.ty);
        this.sfx.play(m.sfx, { x: m.tx * TILE + TILE / 2, y: m.ty * TILE + TILE / 2 });
        break;
      case 'mobHit': {
        const mob = this.mobs.get(m.id);
        if (mob) {
          mob.sprite.setTintFill(m.crit ? 0xffe070 : 0xffffff);
          this.time.delayedCall(80, () => mob.sprite.active && (this.hoverMob === mob ? mob.sprite.setTint(0xff9090) : mob.sprite.clearTint()));
          this.sfx.play(m.crit ? 'crit' : 'hit', { x: mob.goalX, y: mob.goalY });
        }
        break;
      }
      case 'hurt':
        this.shake(120, 0.004);
        if (Settings.get().hurtFlash) this.cameras.main.flash(140, 120, 0, 0, false);
        break;
      case 'died':
        this.cameras.main.fadeIn(1400, 0, 0, 0);
        toast(`You were struck down by a ${m.by.toLowerCase()}. You wake up at home, Wounded for ${Math.round(m.wounded / 60)} minutes: less health, slower healing.`, 8000);
        break;
      case 'fx':
        if (m.kind === 'warn') this.warn(m.x, m.y, m.angle);
        else if (m.kind === 'boss_death') this.bossBoom(m.x, m.y, m.angle);
        else if (m.kind === 'arcane_hit') this.boom('arcane', m.x, m.y, 26) || this.playFx(m.kind, m.x, m.y);
        else if (m.kind === 'wand_hit') this.boom('wand', m.x, m.y, 20) || this.playFx(m.kind, m.x, m.y);
        else if (m.kind === 'skull_hit') this.boom('wand', m.x, m.y, 26) || this.playFx('bolt_hit', m.x, m.y);
        else if (m.kind === 'fireball_hit') this.boom('fire', m.x, m.y, Math.max(30, m.angle)) || this.playFx('bolt_hit', m.x, m.y);
        else if (m.kind === 'siphon_hit') this.playFx('growth', m.x, m.y);
        else if (m.kind === 'meteor') {
          // The server puts meteor fx 20px above the impact; the fire column stands on the ground.
          if (!this.boom('meteor', m.x, m.y + 30, 50, 0, 1)) this.playFx(m.kind, m.x, m.y);
          this.boom('fire', m.x, m.y + 20, 70);
        } else this.playFx(m.kind, m.x, m.y, m.angle);
        break;
      case 'sfx':
        this.sfx.play(m.name, m.x !== undefined && m.y !== undefined ? { x: m.x, y: m.y } : undefined);
        break;
      case 'float':
        this.float(m.x, m.y, m.text, m.color);
        break;
      case 'toast':
        toast(m.text);
        break;
      case 'chat':
        chatLine(m.name, m.text);
        break;
    }
  }

  private addChunk(cx: number, cy: number, ground: Uint8Array, obj: Uint8Array): void {
    const k = cy * CHUNKS_X + cx;
    this.dropChunk(k);
    const data: number[][] = [];
    for (let y = 0; y < CHUNK; y++) {
      const row: number[] = [];
      for (let x = 0; x < CHUNK; x++) {
        const frames = this.groundFrames[ground[y * CHUNK + x]] ?? [0];
        row.push(frames[vhash(cx * CHUNK + x, cy * CHUNK + y) % frames.length]);
      }
      data.push(row);
    }
    const map = this.make.tilemap({ data, tileWidth: TILE, tileHeight: TILE });
    const ts = map.addTilesetImage('ground', 'ground-x', TILE, TILE, PAD, 2 * PAD)!;
    map.createLayer(0, ts, cx * CHUNK * TILE, cy * CHUNK * TILE)!.setDepth(-1e7);
    const chunk: Chunk = { cx, cy, ground, obj, map, objs: new Map(), soils: new Map(), edges: null };
    this.chunks.set(k, chunk);
    // Edges along chunk borders depend on the neighbours, so redo those that are loaded too.
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const n = this.chunks.get((cy + dy) * CHUNKS_X + cx + dx);
      if (n && cx + dx >= 0 && cx + dx < CHUNKS_X) this.drawEdges(n);
    }
    for (let i = 0; i < CHUNK * CHUNK; i++) if (obj[i]) this.placeObj(chunk, i);
  }

  private dropChunk(k: number): void {
    const c = this.chunks.get(k);
    if (!c) return;
    for (const img of c.objs.values()) img.destroy();
    for (const img of c.soils.values()) img.destroy();
    for (const i of c.objs.keys()) this.lamps.delete((c.cy * CHUNK + Math.floor(i / CHUNK)) * 100000 + c.cx * CHUNK + (i % CHUNK));
    c.map.destroy();
    c.edges?.destroy();
    this.chunks.delete(k);
  }

  private drawEdges(c: Chunk): void {
    c.edges?.destroy();
    c.edges = this.groundEdges.draw(c.cx, c.cy, (tx, ty) => this.groundAt(tx, ty));
  }

  /** Ground id at a tile, or -1 if its chunk isn't loaded. */
  private groundAt(tx: number, ty: number): number {
    if (tx < 0 || ty < 0) return -1;
    const c = this.chunks.get(Math.floor(ty / CHUNK) * CHUNKS_X + Math.floor(tx / CHUNK));
    if (!c || tx >= (c.cx + 1) * CHUNK) return -1;
    return c.ground[(ty % CHUNK) * CHUNK + (tx % CHUNK)];
  }

  /** Item pipes and energy cables: one sheet frame per 4-bit neighbour mask (1 E, 2 S, 4 W, 8 N). */
  private lineFrame(o: number, tx: number, ty: number): [string, number] {
    const joins = o === OBJ.CABLE ? POWER_OBJS : NET_OBJS;
    let m = 0;
    [[1, 0], [0, 1], [-1, 0], [0, -1]].forEach(([dx, dy], k) => {
      if (joins.has(this.objAt(tx + dx, ty + dy))) m |= 1 << k;
    });
    return [o === OBJ.CABLE ? 'cable' : 'pipe', m];
  }

  /** A stone wall with wall to its north or south and none east or west is drawn side-on. */
  private wallFrame(tx: number, ty: number, frame: string): string {
    const side = this.objFrames.stone_wall_side;
    if (!side) return frame;
    const wall = (x: number, y: number) => WALL_OBJS.has(this.objAt(x, y));
    const ns = wall(tx, ty - 1) || wall(tx, ty + 1);
    return ns && !wall(tx - 1, ty) && !wall(tx + 1, ty) ? side[0] : frame;
  }

  /** Redraw the lines and walls around a tile whose neighbours changed. */
  private refreshLines(tx: number, ty: number): void {
    for (const [dx, dy] of [[1, 0], [0, 1], [-1, 0], [0, -1]]) {
      const x = tx + dx, y = ty + dy;
      const o = this.objAt(x, y);
      if (o === OBJ.STONE_WALL) {
        const frames = this.objFrames.stone_wall;
        if (frames) this.objImage(x, y)?.setTexture('objects', this.wallFrame(x, y, frames[vhash(x * 7 + 3, y * 13 + 1) % frames.length]));
        continue;
      }
      if (o !== OBJ.CONDUIT && o !== OBJ.CABLE) continue;
      const [key, m] = this.lineFrame(o, x, y);
      this.objImage(x, y)?.setTexture(key, m);
    }
  }

  private placeObj(c: Chunk, i: number): void {
    const def = OBJECTS[c.obj[i]];
    if (def?.key === 'conduit' || def?.key === 'cable') {
      const tx = c.cx * CHUNK + (i % CHUNK), ty = c.cy * CHUNK + Math.floor(i / CHUNK);
      const [key, m] = this.lineFrame(c.obj[i], tx, ty);
      c.objs.set(i, this.add.image(tx * TILE + TILE / 2, ty * TILE + TILE / 2, key, m).setDepth(-4e6));
      return;
    }
    const frames = def && this.objFrames[def.key];
    if (!frames) return;
    const tx = c.cx * CHUNK + (i % CHUNK);
    const ty = c.cy * CHUNK + Math.floor(i / CHUNK);
    const h = vhash(tx * 7 + 3, ty * 13 + 1);
    const frame = def.key === 'stone_wall' ? this.wallFrame(tx, ty, frames[h % frames.length]) : frames[h % frames.length];
    const isFlat = frame.startsWith('flat-');
    // Small jitter so forests don't sit on a visible grid.
    const still = def.kind === 'ruin' || def.kind === 'crop' || def.key === 'soil' || def.key === 'stone_wall';
    const jx = still ? 0 : ((h >>> 8) % 11) - 5;
    const jy = still ? 0 : ((h >>> 12) % 7) - 3;
    const img = isFlat ? this.add.image(tx * TILE + TILE / 2 + jx, ty * TILE + TILE + jy - 2, frame) : this.add.image(tx * TILE + TILE / 2 + jx, ty * TILE + TILE + jy - 2, 'objects', frame);
    img.setOrigin(0.5, 1);
    if (!isFlat && (h >>> 20) & 1 && def.kind !== 'ruin') img.setFlipX(true);
    // Flat things (stumps, decor) sort under people standing on the same row.
    img.setDepth(def.kind === 'decor' || def.kind === 'stump' ? img.y - TILE : img.y);
    if (def.key === 'soil') img.setOrigin(0.5, 0.5).setPosition(tx * TILE + TILE / 2, ty * TILE + TILE / 2).setFlipX(false).setDepth(-5e6);
    if (def.soil && this.objFrames.soil) {
      const f = this.objFrames.soil[0];
      const under = f.startsWith('flat-') ? this.add.image(tx * TILE + TILE / 2, ty * TILE + TILE / 2, f) : this.add.image(tx * TILE + TILE / 2, ty * TILE + TILE / 2, 'objects', f);
      c.soils.set(i, under.setDepth(-5e6));
      img.setY(ty * TILE + TILE - 6);
    }
    c.objs.set(i, img);
    // Water wheels and windmill sails turn.
    if (def.key === 'water_wheel') {
      img.setOrigin(0.5, 0.5).setY(ty * TILE + TILE / 2 - 6);
      const tw = this.tweens.add({ targets: img, angle: 360, duration: 6000, repeat: -1 });
      img.once('destroy', () => tw.remove());
    }
    if (def.key === 'windmill' && this.objFrames.windmill_rotor) {
      const rotor = this.add.image(img.x, img.y - img.height + 10, 'objects', this.objFrames.windmill_rotor[0]).setDepth(img.depth + 1);
      const tw = this.tweens.add({ targets: rotor, angle: 360, duration: 4500, repeat: -1 });
      img.once('destroy', () => {
        tw.remove();
        rotor.destroy();
      });
    }
    if (def.key === 'portal') {
      // No portal sprite in the packs: a stone dais with the ritual swirl turned blue over it.
      const fx = this.playFx('ritual', tx * TILE + TILE / 2, ty * TILE + TILE / 2 - 8);
      if (fx) {
        fx.setTint(0x5aa0ff).setScale(1.9).setAlpha(0.85).setDepth(img.depth + 1);
        img.once('destroy', () => fx.destroy());
      }
    }
    const bloodLit = def.key === 'blood_lamp' || def.key === 'blood_altar';
    if (def.light) this.lamps.set(ty * 100000 + tx, { x: tx * TILE + TILE / 2, y: ty * TILE + (def.key === 'torch' || def.key === 'blood_lamp' ? 0 : TILE / 2), r: def.light, ...(bloodLit ? { tint: 0xff2838 } : {}) });
  }

  private setTile(tx: number, ty: number, o: number): void {
    const c = this.chunks.get(Math.floor(ty / CHUNK) * CHUNKS_X + Math.floor(tx / CHUNK));
    if (!c) return;
    const i = (ty % CHUNK) * CHUNK + (tx % CHUNK);
    c.obj[i] = o;
    const old = c.objs.get(i);
    c.soils.get(i)?.destroy();
    c.soils.delete(i);
    this.lamps.delete(ty * 100000 + tx);
    if (old) {
      if (old === this.hover) this.hover = null;
      const fall = old;
      c.objs.delete(i);
      this.tweens.add({ targets: fall, alpha: 0, scaleY: 0.85, duration: 260, onComplete: () => fall.destroy() });
    }
    if (o) this.placeObj(c, i);
    this.refreshLines(tx, ty);
  }

  objAt(tx: number, ty: number): number {
    const c = this.chunks.get(Math.floor(ty / CHUNK) * CHUNKS_X + Math.floor(tx / CHUNK));
    return c ? c.obj[(ty % CHUNK) * CHUNK + (tx % CHUNK)] : 0;
  }

  private objImage(tx: number, ty: number): Phaser.GameObjects.Image | undefined {
    const c = this.chunks.get(Math.floor(ty / CHUNK) * CHUNKS_X + Math.floor(tx / CHUNK));
    return c?.objs.get((ty % CHUNK) * CHUNK + (tx % CHUNK));
  }

  private onHit(tx: number, ty: number): void {
    const img = this.objImage(tx, ty);
    if (!img) return;
    const x0 = img.x;
    img.setTintFill(0xfff0c0);
    this.time.delayedCall(70, () => img.active && img.clearTint());
    this.tweens.add({ targets: img, x: x0 + 3, duration: 45, yoyo: true, repeat: 1, onComplete: () => img.active && img.setX(x0) });
  }

  /** Camera shake, scaled (or switched off) by the player's setting. */
  private shake(ms: number, intensity: number): void {
    const k = Settings.get().shake;
    if (k > 0) this.cameras.main.shake(ms, intensity * k);
  }

  private float(x: number, y: number, text: string, color = '#ffffff'): void {
    // Damage and healing numbers can be turned off; names of loot and other words always show.
    if (!Settings.get().damageNumbers && /^[-+]?\d+$/.test(text)) return;
    const t = this.add.text(x, y, text, { fontFamily: 'Georgia, serif', fontSize: '15px', color, stroke: '#000000', strokeThickness: 4 }).setOrigin(0.5, 1).setDepth(1e7);
    this.tweens.add({ targets: t, y: y - 40, alpha: 0, duration: 1400, ease: 'Cubic.easeOut', onComplete: () => t.destroy() });
  }

  // ---------------------------------------------------------------- people

  private onSnap(players: PlayerSnap[]): void {
    this.snapCount++;
    for (const p of players) {
      let a = this.actors.get(p.id);
      if (!a) {
        this.ensureHero(p.hero);
        const key = this.textures.exists(`char-${p.hero}`) ? `char-${p.hero}` : this.fallbackChar();
        const sprite = this.add.sprite(p.x, p.y + 14, key, key.startsWith('char-') ? p.face * COLS : undefined).setOrigin(0.5, FEET);
        const label = this.add.text(p.x, p.y - 60, p.name, { fontFamily: 'Georgia, serif', fontSize: '13px', color: p.id === this.myId ? '#f0dca0' : '#cfe0ff', stroke: '#000', strokeThickness: 3 }).setOrigin(0.5, 1);
        a = { id: p.id, hero: p.hero, sprite, label, goalX: p.x, goalY: p.y, face: p.face, anim: 'idle', seen: 0, held: null, tool: null };
        this.actors.set(p.id, a);
        if (p.id === this.myId) this.cameras.main.startFollow(sprite, true, 0.15, 0.15, 0, 20);
      }
      if (Math.hypot(p.x - a.goalX, p.y - a.goalY) > TILE * 4) {
        a.sprite.setPosition(p.x, p.y + 14);
      }
      a.goalX = p.x;
      a.goalY = p.y;
      a.seen = this.snapCount;
      a.held = p.held;
      this.animate(a, p.face, p.anim);
      if (p.id === this.myId) {
        setHp(p.hp, p.maxHp, this.wounded, this.fullHp);
        setText('hud-pos', `${Math.floor(p.x / TILE)}, ${Math.floor(p.y / TILE)}`);
        this.unloadFar(p.x, p.y);
      }
    }
    for (const [id, a] of this.actors) if (a.seen !== this.snapCount) {
      a.sprite.destroy();
      a.label.destroy();
      a.tool?.destroy();
      this.actors.delete(id);
    }
  }

  private animate(a: Actor, face: number, anim: PlayerSnap['anim']): void {
    const key = `char-${a.hero}`;
    if (!this.textures.exists(key) || a.sprite.texture.key !== key) return;
    if (face === a.face && anim === a.anim) return;
    a.face = face;
    a.anim = anim;
    if (anim === 'idle') {
      a.sprite.stop();
      a.sprite.setFrame(face * COLS);
    } else {
      // Keep the walk cycle's phase when only the heading changes.
      const progress = a.sprite.anims.isPlaying ? a.sprite.anims.getProgress() : 0;
      a.sprite.play(`${a.hero}-${anim}-${face}`);
      if (progress) a.sprite.anims.setProgress(progress);
    }
  }

  /** Boss sheets are big, so each loads the first time that boss is seen. One row per animation. */
  private ensureBoss(sheet: string): void {
    const key = `boss-${sheet}`;
    const meta = this.bossMeta[sheet];
    if (!meta || this.textures.exists(key) || this.bossLoading.has(sheet)) return;
    this.bossLoading.add(sheet);
    const [fw, fh] = meta.frame;
    this.load.spritesheet(key, `/art/boss-${sheet}.png`, { frameWidth: fw, frameHeight: fh });
    this.load.once(`filecomplete-spritesheet-${key}`, () => {
      const cols = this.textures.get(key).getSourceImage().width / fw;
      for (const [name, [row, n, fps, loop]] of Object.entries(meta.anims)) {
        this.anims.create({ key: `${key}-${name}`, frames: this.anims.generateFrameNumbers(key, { start: row * cols, end: row * cols + n - 1 }), frameRate: fps, repeat: loop ? -1 : 0 });
      }
      for (const m of this.mobs.values()) if (m.boss === sheet) {
        m.sprite.setTexture(key, 0).setOrigin(meta.pivot[0] / fw, meta.pivot[1] / fh).setScale(1);
        m.anim = '';
      }
    });
    this.load.start();
  }

  /** Is one of these objects within n tiles of me (for crafting stations)? */
  nearObj(obj: number, n: number): boolean {
    const me = this.actors.get(this.myId);
    if (!me) return false;
    const tx = Math.floor(me.goalX / TILE), ty = Math.floor(me.goalY / TILE);
    for (let y = ty - n; y <= ty + n; y++) for (let x = tx - n; x <= tx + n; x++) if (this.objAt(x, y) === obj) return true;
    return false;
  }

  private onMobs(list: MobSnap[]): void {
    for (const s of list) {
      let m = this.mobs.get(s.id);
      if (!m && s.boss) {
        this.ensureBoss(s.boss);
        const key = `boss-${s.boss}`, meta = this.bossMeta[s.boss];
        const ready = this.textures.exists(key) && meta;
        const sprite = this.add.sprite(s.x, s.y + 14, ready ? key : this.fallbackChar(), ready ? 0 : undefined);
        if (ready) sprite.setOrigin(meta.pivot[0] / meta.frame[0], meta.pivot[1] / meta.frame[1]);
        else sprite.setOrigin(0.5, FEET).setScale(1.6);
        const bar = this.add.graphics();
        const label = this.add.text(s.x, s.y, s.name, { fontFamily: 'Georgia, serif', fontSize: '15px', color: '#ff9a5a', stroke: '#000', strokeThickness: 4 }).setOrigin(0.5, 1);
        m = { id: s.id, slug: s.slug, name: s.name, sprite, bar, label, goalX: s.x, goalY: s.y, face: s.face, anim: '', hp: s.hp, max: s.max, elite: true, scale: 2.2, seen: 0, boss: s.boss, title: s.title, phase: s.phase };
        this.mobs.set(s.id, m);
      }
      if (!m) {
        this.ensureHero(s.slug);
        const key = this.textures.exists(`char-${s.slug}`) ? `char-${s.slug}` : this.fallbackChar();
        const sprite = this.add.sprite(s.x, s.y + 14, key, key.startsWith('char-') ? s.face * COLS : undefined).setOrigin(0.5, FEET).setScale(s.scale);
        const bar = this.add.graphics();
        const label = this.add.text(s.x, s.y, s.name, { fontFamily: 'Georgia, serif', fontSize: '13px', color: s.elite ? '#ffb070' : '#ffd0c8', stroke: '#000', strokeThickness: 3 }).setOrigin(0.5, 1).setVisible(s.elite);
        m = { id: s.id, slug: s.slug, name: s.name, sprite, bar, label, goalX: s.x, goalY: s.y, face: s.face, anim: '', hp: s.hp, max: s.max, elite: s.elite, scale: s.scale, seen: 0 };
        this.mobs.set(s.id, m);
      }
      if (Math.hypot(s.x - m.goalX, s.y - m.goalY) > TILE * 4) m.sprite.setPosition(s.x, s.y + 14);
      m.goalX = s.x;
      m.goalY = s.y;
      m.hp = s.hp;
      m.max = s.max;
      m.seen = this.snapCount;
      if (m.boss) {
        m.phase = s.phase;
        this.animateBoss(m, s.face, s.anim);
      } else this.animateMob(m, s.face, s.anim);
    }
    for (const [id, m] of this.mobs) if (m.seen !== this.snapCount) {
      if (this.hoverMob === m) this.hoverMob = null;
      this.mobs.delete(id);
      const parts = [m.sprite, m.bar, m.label];
      this.tweens.add({ targets: parts, alpha: 0, duration: 400, onComplete: () => parts.forEach((p) => p.destroy()) });
    }
  }

  private animateMob(m: MobActor, face: number, anim: MobSnap['anim']): void {
    const key = `char-${m.slug}`;
    if (!this.textures.exists(key) || m.sprite.texture.key !== key) return;
    if (face === m.face && anim === m.anim) return;
    if (m.anim === 'death') return;
    m.face = face;
    m.anim = anim;
    if (anim === 'idle') {
      m.sprite.stop();
      m.sprite.setFrame(face * COLS + 1);
    } else m.sprite.play(`${m.slug}-${anim}-${face}`);
  }

  private animateBoss(m: MobActor, face: number, anim: MobSnap['anim']): void {
    const key = `boss-${m.boss}`;
    if (!this.textures.exists(key) || m.sprite.texture.key !== key) return;
    if (face === 3 || face === 4 || face === 5) m.sprite.setFlipX(true);
    else if (face === 0 || face === 1 || face === 7) m.sprite.setFlipX(false);
    m.face = face;
    if (m.anim === 'death') return;
    const two = (m.phase ?? 1) >= 2;
    const base = { idle: 'idle', walk: 'walk', attack: 'attack_quick', heavy: 'attack_heavy', special: 'special', death: 'death' }[anim as string];
    const row = anim === 'transform' ? 'transform' : anim === 'new' ? 'p2_new' : `${two ? 'p2_' : ''}${base ?? 'idle'}`;
    const want = `${key}-${row}`;
    if (m.anim === anim && m.sprite.anims.currentAnim?.key === want) return;
    m.anim = anim;
    if (this.anims.exists(want)) m.sprite.play(want);
  }

  /** A boss's area attack: the ground glows red where it will land, then it lands. */
  private warn(x: number, y: number, r: number): void {
    const g = this.add.graphics().setDepth(1);
    const t0 = this.time.now;
    const tick = () => {
      const k = Math.min(1, (this.time.now - t0) / 1100);
      g.clear();
      g.fillStyle(0xff2a1a, 0.12 + k * 0.28).fillCircle(x, y, r);
      g.lineStyle(3, 0xff6a3a, 0.9).strokeCircle(x, y, r);
      g.fillStyle(0xff8a4a, 0.35).fillCircle(x, y, r * k);
    };
    const ev = this.time.addEvent({ delay: 30, loop: true, callback: tick });
    tick();
    this.time.delayedCall(1100, () => {
      ev.remove();
      g.destroy();
      if (!this.boom('fire', x, y, r * 0.8)) this.playFx('bolt_hit', x, y);
      const ring = this.add.circle(x, y, r * 0.3, 0xffa060, 0.5).setDepth(1);
      this.tweens.add({ targets: ring, radius: r * 1.1, alpha: 0, duration: 350, onComplete: () => ring.destroy() });
      const me = this.actors.get(this.myId);
      if (me && Math.hypot(me.goalX - x, me.goalY - y) < r * 2.5) this.shake(220, 0.006);
    });
  }

  private drawMob(m: MobActor, k: number): void {
    m.sprite.x += (m.goalX - m.sprite.x) * k;
    m.sprite.y += (m.goalY + 14 - m.sprite.y) * k;
    m.sprite.setDepth(m.sprite.y - 14);
    const top = m.sprite.y - 70 * m.scale;
    m.bar.clear();
    if (m.hp < m.max && m.anim !== 'death' && !m.boss) {
      m.bar.fillStyle(0x000000, 0.7).fillRect(m.sprite.x - 21, top - 3, 42, 6);
      m.bar.fillStyle(m.elite ? 0xff8a3a : 0xd83a2a, 1).fillRect(m.sprite.x - 20, top - 2, 40 * (m.hp / m.max), 4);
    }
    m.bar.setDepth(1e6);
    m.label.setPosition(m.sprite.x, top - 5).setDepth(1e6).setVisible(m.anim !== 'death' && (m.elite || this.hoverMob === m));
  }

  /** Items lying on the ground: a small bobbing icon with a glow (blue magic, gold rare) and a count. */
  private onDrops(list: Extract<ServerMsg, { t: 'snap' }>['drops']): void {
    for (const d of list) {
      let e = this.drops.get(d.id);
      if (!e) {
        const color = d.r === 3 ? 0xff9a3a : d.r === 2 ? 0xffd84a : d.r === 1 ? 0x6a98ff : 0xfff0c0;
        const text = d.r === 3 ? '#ff9a3a' : d.r === 2 ? '#ffd84a' : d.r === 1 ? '#9abaff' : '#fff';
        const glow = this.add.ellipse(d.x, d.y + 10, 30, 12, color, d.r ? 0.45 : 0.25).setDepth(d.y - 1);
        const icon = this.textures.get('icons').has(d.i) ? this.add.image(d.x, d.y, 'icons', d.i) : this.add.image(d.x, d.y, this.fallbackChar());
        icon.setDisplaySize(30, 30).setDepth(d.y);
        const label = this.add.text(d.x + 12, d.y + 4, '', { fontFamily: 'Georgia, serif', fontSize: '11px', color: text, stroke: '#000', strokeThickness: 3 }).setOrigin(0, 0).setDepth(d.y + 1);
        const title = d.name ?? ITEMS[d.i as ItemId]?.name ?? d.i;
        const name = this.add.text(d.x, d.y - 20, title, { fontFamily: 'Georgia, serif', fontSize: '12px', color: text, stroke: '#000', strokeThickness: 3 }).setOrigin(0.5, 1).setDepth(1e6).setVisible(false);
        e = { icon, glow, label, name, seen: 0, x: d.x, y: d.y };
        this.drops.set(d.id, e);
      }
      const text = d.n > 1 ? String(d.n) : '';
      if (e.label.text !== text) e.label.setText(text);
      e.seen = this.snapCount;
    }
    for (const [id, e] of this.drops) if (e.seen !== this.snapCount) {
      e.icon.destroy();
      e.glow.destroy();
      e.label.destroy();
      e.name.destroy();
      this.drops.delete(id);
    }
  }

  /** The nearest living boss gets a big health bar across the top of the screen. */
  private drawBossBar(): void {
    const me = this.actors.get(this.myId);
    let best: MobActor | null = null;
    if (me) for (const m of this.mobs.values()) if (m.boss && m.anim !== 'death' && Math.hypot(m.goalX - me.goalX, m.goalY - me.goalY) < TILE * 16) best = m;
    const el = document.getElementById('boss-bar');
    if (!el) return;
    el.classList.toggle('hidden', !best);
    if (!best) return;
    const text = `${best.name}${best.phase === 2 ? ' — enraged' : ''}`;
    const fill = `${Math.max(0, (best.hp / best.max) * 100).toFixed(1)}%`;
    const name = document.getElementById('boss-name')!, sub = document.getElementById('boss-title')!, bar = document.getElementById('boss-fill')!;
    if (name.textContent !== text) name.textContent = text;
    if (sub.textContent !== (best.title ?? '')) sub.textContent = best.title ?? '';
    if (bar.style.width !== fill) bar.style.width = fill;
    el.classList.toggle('enraged', best.phase === 2);
  }

  private mobAt(p: Phaser.Input.Pointer): MobActor | null {
    const w = this.cameras.main.getWorldPoint(p.x, p.y);
    let best: MobActor | null = null;
    let bd = Infinity;
    for (const m of this.mobs.values()) {
      if (m.anim === 'death') continue;
      const s = m.boss ? 1.8 : m.scale;
      const dx = w.x - m.sprite.x, dy = w.y - (m.sprite.y - 30 * s);
      if (Math.abs(dx) < 24 * s && Math.abs(dy) < 40 * s && Math.hypot(dx, dy) < bd) {
        bd = Math.hypot(dx, dy);
        best = m;
      }
    }
    return best;
  }

  playFx(kind: string, x: number, y: number, angle = 0): Phaser.GameObjects.Sprite | null {
    if (!this.anims.exists(`fx-${kind}`)) return null;
    const s = this.add.sprite(x, y, `fx-${kind}`).setBlendMode(Phaser.BlendModes.ADD).setDepth(9e5 + 5).setScale(1.4).setRotation(angle);
    s.play(`fx-${kind}`);
    if (kind !== 'bolt' && kind !== 'ritual') s.once('animationcomplete', () => s.destroy());
    return s;
  }

  /**
   * A blast from the explosions pack, scaled so its art reaches `r` world px. `oy` is the anchor
   * (0.5 = centred; 1 = standing on the point, for the meteor's fire column). Returns null if the
   * art isn't loaded, so callers can fall back to the older spell-FX strips.
   */
  boom(look: string, x: number, y: number, r: number, delay = 0, oy = 0.5): Phaser.GameObjects.Sprite | null {
    const b = this.boomMeta[look];
    if (!b || !this.anims.exists(`boom-${look}`)) return null;
    const s = this.add.sprite(x, y, `boom-${look}`).setOrigin(0.5, oy).setScale(r / b.r).setDepth(9e5 + 5).setVisible(false);
    s.once('animationcomplete', () => s.destroy());
    const go = () => {
      s.setVisible(true).play(`boom-${look}`);
      this.flashes.push({ x, y: oy === 1 ? y - r : y, r: r * 2.4, tint: BOOM_TINT[look] ?? 0xffa040, t0: this.time.now, dur: 450 });
    };
    if (delay > 0) this.time.delayedCall(delay, go);
    else go();
    return s;
  }

  /** A boss going up (Radville's look): one big blast, then smaller pops scattered around it, and the screen shakes. */
  private bossBoom(x: number, y: number, r: number): void {
    this.boom('big', x, y - 6, r * 2.2);
    for (let i = 0; i < 5; i++) {
      const a = x * 0.37 + i * 2.1;
      const d = r * (0.8 + (i % 3) * 0.35);
      this.boom('small', x + Math.cos(a) * d, y - 4 + Math.sin(a) * d * 0.7, r * (0.7 + (i % 2) * 0.3), 120 + i * 110);
    }
    const me = this.actors.get(this.myId);
    const dist = me ? Math.hypot(me.goalX - x, me.goalY - y) : 0;
    if (dist < 900) this.shake(700, 0.004 + 0.012 * (1 - dist / 900));
    this.sfx.play('hurt', { x, y });
  }

  /** Blood bolts in flight, altar readouts and awake ritual stones, from the snapshot. */
  private onMagic(m: Extract<ServerMsg, { t: 'snap' }>): void {
    for (const b of m.bolts) {
      let e = this.bolts.get(b.id);
      if (!e) {
        const look = SHOT_LOOK[b.k ?? 'bolt'], sh = this.shotMeta.looks[look];
        let sprite: Phaser.GameObjects.Sprite | null, tint: number;
        if (sh && this.anims.exists(`shot-${look}`)) {
          // Projectile art: the bright head sits on the bolt's real position.
          sprite = this.add.sprite(b.x, b.y, 'shots').setOrigin(sh.ax / 16, sh.ay / 16).setScale(SHOT_SCALE[b.k ?? 'bolt'] ?? 2).setRotation(b.a).setDepth(9e5 + 5);
          sprite.play({ key: `shot-${look}`, startFrame: b.id % sh.n });
          tint = (sh.rgb[0] << 16) | (sh.rgb[1] << 8) | sh.rgb[2];
        } else {
          sprite = this.playFx(b.k === 'lance' ? 'bolt' : b.k ?? 'bolt', b.x, b.y, b.a);
          if (!sprite) continue;
          if (b.k === 'lance') sprite.setScale(1.6);
          else if (b.k === 'wand') sprite.setScale(0.7);
          tint = b.k === 'arcane' ? 0x4a8aff : b.k === 'wand' ? 0xa060ff : 0xff2030;
        }
        e = { sprite, seen: 0, gx: b.x, gy: b.y, tint };
        this.bolts.set(b.id, e);
      }
      e.gx = b.x;
      e.gy = b.y;
      e.seen = this.snapCount;
      e.sprite.setRotation(b.a);
    }
    for (const [id, e] of this.bolts) if (e.seen !== this.snapCount) {
      e.sprite.destroy();
      this.bolts.delete(id);
    }
    const seen = new Set<string>();
    const label = (key: string, x: number, y: number, text: string, color: string) => {
      seen.add(key);
      let t = this.siteLabels.get(key);
      if (!t) {
        t = this.add.text(x, y, '', { fontFamily: 'Georgia, serif', fontSize: '12px', color, stroke: '#000', strokeThickness: 3, align: 'center' }).setOrigin(0.5, 1).setDepth(1e6);
        this.siteLabels.set(key, t);
      }
      if (t.text !== text) t.setText(text);
    };
    for (const a of m.altars) {
      const item = a.item ? `\n${ITEMS[a.item as ItemId]?.name ?? a.item}${a.pct < 100 ? ` ${a.pct}%` : ' — ready'}` : '';
      label(`a${a.tx},${a.ty}`, a.tx * TILE + TILE / 2, a.ty * TILE - 26, `Tier ${a.tier} altar · ${a.lp}/${a.cap} LP${item}`, '#ff8a9a');
    }
    for (const r of m.rituals) {
      const key = `r${r.tx},${r.ty}`;
      const look = RITUAL_LOOK[r.k ?? 'grove'];
      label(key, r.tx * TILE + TILE / 2, r.ty * TILE - 18, r.lp > 0 ? `${look.name} · ${r.lp} LP` : `${look.name} (asleep)`, r.lp > 0 ? look.color : '#a49a7c');
      const fx = this.ritualFx.get(key);
      if (r.lp > 0 && !fx) {
        const s = this.playFx('ritual', r.tx * TILE + TILE / 2, r.ty * TILE + TILE / 2);
        if (s) this.ritualFx.set(key, s.setAlpha(0.6).setDepth(-3e6).setTint(look.tint));
      } else if (fx) fx.setTint(look.tint);
      if (r.lp <= 0 && fx) {
        fx.destroy();
        this.ritualFx.delete(key);
      }
    }
    for (const [key, t] of this.siteLabels) if (!seen.has(key)) {
      t.destroy();
      this.siteLabels.delete(key);
      this.ritualFx.get(key)?.destroy();
      this.ritualFx.delete(key);
    }
  }

  private claimGfx: Phaser.GameObjects.GameObject[] = [];
  private claimList: { x: number; y: number; w: number; h: number; owner: string; mine: boolean }[] = [];

  /** Claimed ruins get a dashed border in the owner's colour and a name plate. */
  private drawClaims(list: typeof this.claimList): void {
    for (const g of this.claimGfx) g.destroy();
    this.claimGfx = [];
    this.claimList = list;
    for (const c of list) {
      const color = c.mine ? 0xe0b85a : 0x7aa0e0;
      const g = this.add.graphics().setDepth(-1e6);
      g.lineStyle(3, color, 0.85);
      const x0 = c.x * TILE, y0 = c.y * TILE, x1 = (c.x + c.w) * TILE, y1 = (c.y + c.h) * TILE;
      const dash = (ax: number, ay: number, bx: number, by: number) => {
        const len = Math.hypot(bx - ax, by - ay);
        for (let t = 0; t < len; t += 18) {
          const t2 = Math.min(len, t + 10);
          g.lineBetween(ax + ((bx - ax) * t) / len, ay + ((by - ay) * t) / len, ax + ((bx - ax) * t2) / len, ay + ((by - ay) * t2) / len);
        }
      };
      dash(x0, y0, x1, y0);
      dash(x1, y0, x1, y1);
      dash(x1, y1, x0, y1);
      dash(x0, y1, x0, y0);
      g.fillStyle(color, 0.06).fillRect(x0, y0, x1 - x0, y1 - y0);
      const label = this.add
        .text((x0 + x1) / 2, y0 - 4, c.mine ? 'Your ruin' : `${c.owner}'s ruin`, { fontFamily: 'Georgia, serif', fontSize: '14px', color: c.mine ? '#f0dca0' : '#cfe0ff', stroke: '#000', strokeThickness: 3 })
        .setOrigin(0.5, 1)
        .setDepth(1e6 - 1);
      this.claimGfx.push(g, label);
    }
  }

  private claimNameAt(x: number, y: number): string {
    const tx = x / TILE, ty = y / TILE;
    const c = this.claimList.find((k) => tx >= k.x && tx < k.x + k.w && ty >= k.y && ty < k.y + k.h);
    return c ? (c.mine ? ' · your ruin' : ` · ${c.owner}'s ruin`) : '';
  }

  /** The held item swings at the hero's side while working or fighting. */
  private drawTool(a: Actor): void {
    const show = a.anim === 'attack' && a.held && this.textures.exists('icons') && this.textures.get('icons').has(a.held);
    if (!show) {
      a.tool?.setVisible(false);
      return;
    }
    if (!a.tool) a.tool = this.add.image(0, 0, 'icons', a.held!).setOrigin(0.15, 0.85).setScale(0.6);
    if (a.tool.frame.name !== a.held) a.tool.setFrame(a.held!);
    const d = FACE_DIR[a.face];
    const left = d.x < -0.1 || (Math.abs(d.x) <= 0.1 && d.y < 0);
    const phase = ((this.time.now / 1000) % SWING_TIME) / SWING_TIME;
    // Wind up, then a quick chop down.
    const swing = phase < 0.55 ? -1.1 * (phase / 0.55) : -1.1 + 2.0 * Math.min(1, (phase - 0.55) / 0.2);
    a.tool.setVisible(true);
    a.tool.setFlipX(left);
    a.tool.setOrigin(left ? 0.85 : 0.15, 0.85);
    a.tool.setRotation(left ? -swing : swing);
    a.tool.setPosition(a.sprite.x + d.x * 14 + (left ? -6 : 6), a.sprite.y - 30 + d.y * 8);
    a.tool.setDepth(a.sprite.depth + (d.y < -0.1 ? -1 : 1));
  }

  private stepSound(x: number, y: number): string {
    const tx = Math.floor(x / TILE), ty = Math.floor(y / TILE);
    const c = this.chunks.get(Math.floor(ty / CHUNK) * CHUNKS_X + Math.floor(tx / CHUNK));
    const g = c ? c.ground[(ty % CHUNK) * CHUNK + (tx % CHUNK)] : G.GRASS;
    if (g === G.SHALLOW || g === G.MUD) return 'step_water';
    if (g === G.STONE || g === G.ROCK || g === G.CINDER) return 'step_stone';
    if (g === G.DIRT || g === G.SAND || g === G.ASH) return 'step_dirt';
    return 'step_grass';
  }

  private fallbackChar(): string {
    if (!this.textures.exists('char-flat')) {
      const g = this.add.graphics();
      g.fillStyle(0x000000, 0.35).fillEllipse(64, 112, 34, 12);
      g.fillStyle(0xd8c8a0, 1).fillRoundedRect(50, 60, 28, 50, 8);
      g.fillStyle(0xe8d8b8, 1).fillCircle(64, 52, 12);
      g.generateTexture('char-flat', 128, 128);
      g.destroy();
    }
    return 'char-flat';
  }

  private unloadFar(x: number, y: number): void {
    const pcx = Math.floor(x / TILE / CHUNK), pcy = Math.floor(y / TILE / CHUNK);
    for (const [k, c] of this.chunks) if (Math.abs(c.cx - pcx) > VIEW_CHUNKS + 1 || Math.abs(c.cy - pcy) > VIEW_CHUNKS + 1) this.dropChunk(k);
  }

  /** Trees and walls in front of a person go see-through, so nobody vanishes into a forest. */
  private faded = new Set<Phaser.GameObjects.Image>();
  private fadeOccluders(): void {
    const now = new Set<Phaser.GameObjects.Image>();
    for (const a of this.actors.values()) {
      const px = a.sprite.x, py = a.sprite.y - 30;
      const tx = Math.floor(px / TILE), ty = Math.floor(a.sprite.y / TILE);
      for (let y = ty; y <= ty + 4; y++) for (let x = tx - 2; x <= tx + 2; x++) {
        const img = this.objImage(x, y);
        if (!img || img.depth <= a.sprite.depth || img.displayHeight < 60) continue;
        if (img.getBounds().contains(px, py)) now.add(img);
      }
    }
    for (const img of this.faded) if (!now.has(img) && img.active) img.setAlpha(1);
    for (const img of now) img.setAlpha(0.45);
    this.faded = now;
  }

  private tileAt(p: Phaser.Input.Pointer): { tx: number; ty: number } {
    const w = this.cameras.main.getWorldPoint(p.x, p.y);
    return { tx: Math.floor(w.x / TILE), ty: Math.floor(w.y / TILE) };
  }

  update(_t: number, dtMs: number): void {
    const dt = dtMs / 1000;
    const k = Math.min(1, dt * 14);
    for (const a of this.actors.values()) {
      a.sprite.x += (a.goalX - a.sprite.x) * k;
      a.sprite.y += (a.goalY + 14 - a.sprite.y) * k;
      a.sprite.setDepth(a.sprite.y - 14);
      a.label.setPosition(a.sprite.x, a.sprite.y - 72).setDepth(1e6);
      this.drawTool(a);
    }
    const meA = this.actors.get(this.myId);
    this.minimap.update(this.time.now, meA ? { x: meA.goalX, y: meA.goalY } : null,
      [...this.actors.values()].map((a) => ({ x: a.goalX, y: a.goalY, me: a.id === this.myId, name: a.label.text })));
    for (const m of this.mobs.values()) this.drawMob(m, k);
    // Drops bob a little; hovering one shows what it is (magic items by their name).
    const ptr = this.cameras.main.getWorldPoint(this.input.activePointer.x, this.input.activePointer.y);
    for (const e of this.drops.values()) {
      e.icon.setY(e.y - 4 + Math.sin(this.time.now / 300 + e.y) * 3);
      e.name.setVisible(Math.abs(ptr.x - e.x) < 20 && Math.abs(ptr.y - e.y) < 20);
    }
    for (const b of this.bolts.values()) b.sprite.setPosition(b.sprite.x + (b.gx - b.sprite.x) * Math.min(1, dt * 20), b.sprite.y + (b.gy - b.sprite.y) * Math.min(1, dt * 20));
    if (this.packUi.open && this.snapCount % 10 === 0) this.packUi.refreshStation();
    // Day and night: the clock keeps running between snapshots.
    const now = this.worldTime + (this.time.now - this.worldTimeAt) / 1000;
    const meNow = this.actors.get(this.myId);
    // Dungeons are always dark: braziers, torches and you are the only light. The Twilight Glade is always dusk.
    const below = meNow ? dungeonAt(Math.floor(meNow.goalX / TILE), Math.floor(meNow.goalY / TILE)) : -1;
    const light = below < 0 ? daylight(now) : DUNGEONS[below].realm?.layout === 'glade' ? 0.42 : 0.1;
    this.drawBossBar();
    const lights: Light[] = [];
    for (const a of this.actors.values()) lights.push({ x: a.sprite.x, y: a.sprite.y - 24, r: a.id === this.myId ? 250 : 190, warm: false });
    for (const l of this.lamps.values()) lights.push({ ...l, warm: true });
    for (const b of this.bolts.values()) lights.push({ x: b.sprite.x, y: b.sprite.y, r: 90, warm: true, tint: b.tint });
    this.flashes = this.flashes.filter((f) => this.time.now - f.t0 < f.dur);
    for (const f of this.flashes) lights.push({ x: f.x, y: f.y, r: f.r * (1 - (this.time.now - f.t0) / f.dur), warm: true, tint: f.tint });
    this.lighting.draw(light, lights, this.time.now);
    if (this.worldTimeAt) this.sfx.ambience(light < 0.5 ? 'amb_night' : 'amb_day');
    const me = this.actors.get(this.myId);
    if (me) {
      this.sfx.listener = { x: me.sprite.x, y: me.sprite.y };
      if (me.anim === 'walk') {
        this.stepT -= dt;
        if (this.stepT <= 0) {
          this.stepT = 0.34;
          this.sfx.play(this.stepSound(me.goalX, me.goalY), undefined, 0.8);
        }
      } else this.stepT = 0.1;
    }

    this.fadeOccluders();

    const typing = document.activeElement?.tagName === 'INPUT';
    const K = this.keys;
    const keys = typing
      ? { up: false, down: false, left: false, right: false }
      : { up: K.up.isDown || K.w.isDown, down: K.down.isDown || K.s.isDown, left: K.left.isDown || K.a.isDown, right: K.right.isDown || K.d.isDown };
    const sig = `${+keys.up}${+keys.down}${+keys.left}${+keys.right}`;
    if (sig !== this.sentKeys) {
      this.sentKeys = sig;
      this.net.send({ t: 'keys', ...keys });
    }

    const p = this.input.activePointer;
    if (this.holding && p.isDown && this.time.now - this.lastHold > 250) {
      this.lastHold = this.time.now;
      const { tx, ty } = this.tileAt(p);
      this.net.send({ t: 'move', tx, ty });
    }

    // Hover: monsters first (red, crosshair), then things you can gather.
    const mob = this.mobAt(p);
    if (mob !== this.hoverMob) {
      if (this.hoverMob?.sprite.active) this.hoverMob.sprite.clearTint();
      this.hoverMob = mob;
      if (mob) mob.sprite.setTint(0xff9090);
    }
    const { tx, ty } = this.tileAt(p);
    const img = !mob && harvestable(this.objAt(tx, ty)) ? this.objImage(tx, ty) : undefined;
    if (img !== this.hover) {
      if (this.hover?.active) this.hover.clearTint();
      this.hover = img ?? null;
      if (img) img.setTint(0xfff2c8);
    }
    this.input.setDefaultCursor(mob ? 'crosshair' : img ? 'pointer' : 'default');
  }
}

// ---------------------------------------------------------------- DOM HUD bits

function b64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function setText(id: string, text: string): void {
  const el = document.getElementById(id);
  if (el && el.textContent !== text) el.textContent = text;
}

function toast(text: string, ms = 3500): void {
  const box = document.getElementById('toasts')!;
  const el = document.createElement('div');
  el.textContent = text;
  box.appendChild(el);
  setTimeout(() => (el.style.opacity = '0'), ms);
  setTimeout(() => el.remove(), ms + 700);
}

function renderBuffs(buffs: Record<string, number>, foods: number): void {
  const el = document.getElementById('buffs')!;
  const chips = Object.entries(buffs).map(([id, secs]) => {
    const b = BUFFS[id as BuffId];
    return b ? `<span class="buff" style="border-color:${b.color};color:${b.color}" title="${b.text}">${b.name} ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}</span>` : '';
  });
  if (foods) chips.push(`<span class="buff foods" title="Every new food you eat adds ${VARIETY_HP} max health for good">${foods} foods tried · +${foods * VARIETY_HP} HP</span>`);
  const html = chips.join('');
  if (el.innerHTML !== html) el.innerHTML = html;
}

function chatLine(name: string, text: string): void {
  const log = document.getElementById('chat-log')!;
  const el = document.createElement('div');
  const b = document.createElement('b');
  b.textContent = name + ': ';
  el.append(b, text);
  log.appendChild(el);
  while (log.children.length > 8) log.firstChild!.remove();
}

function setupChat(net: Net, kb: Phaser.Input.Keyboard.KeyboardPlugin): void {
  const input = document.getElementById('chat-input') as HTMLInputElement;
  kb.on('keydown-ENTER', () => {
    if (document.activeElement?.tagName !== 'INPUT') setTimeout(() => input.focus(), 0);
  });
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      const text = input.value.trim();
      if (text) net.send({ t: 'chat', text });
      input.value = '';
      input.blur();
    } else if (e.key === 'Escape') {
      input.value = '';
      input.blur();
    }
  });
}

function showStatus(text: string): void {
  const el = document.getElementById('status')!;
  el.textContent = text;
  el.classList.remove('hidden');
}

export const heroName = (slug: string) => HEROES.find((h) => h.slug === slug)?.name ?? slug;
